import React from 'react';

/**
 * Checkbox Component
 * Reusable checkbox with label
 */
const Checkbox = ({
  label,
  name,
  checked,
  onChange,
  error,
  touched,
  required = false,
  disabled = false,
  helper,
  className = '',
  ...props
}) => {
  const hasError = touched && error;
  const checkboxId = props.id || `checkbox-${name}`;

  return (
    <div className={`mb-4 ${className}`}>
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id={checkboxId}
            name={name}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            required={required}
            className={`
              h-4 w-4 rounded border-bukawa-gray-300
              text-bukawa-primary
              focus:ring-2 focus:ring-bukawa-primary-400 focus:ring-offset-2
              ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              ${hasError ? 'border-red-500' : ''}
            `.trim().replace(/\s+/g, ' ')}
            aria-invalid={hasError ? 'true' : 'false'}
            aria-describedby={
              hasError ? `${checkboxId}-error` : helper ? `${checkboxId}-helper` : undefined
            }
            {...props}
          />
        </div>
        <div className="ml-3">
          <label
            htmlFor={checkboxId}
            className={`
              text-sm font-medium text-bukawa-gray-700
              ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            `.trim().replace(/\s+/g, ' ')}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          
          {/* Helper text */}
          {helper && !hasError && (
            <p
              id={`${checkboxId}-helper`}
              className="mt-1 text-sm text-bukawa-gray-500"
            >
              {helper}
            </p>
          )}
        </div>
      </div>

      {/* Error message */}
      {hasError && (
        <p
          id={`${checkboxId}-error`}
          className="mt-1 ml-7 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Radio Component
 * Reusable radio button with label
 */
export const Radio = ({
  label,
  name,
  value,
  checked,
  onChange,
  disabled = false,
  className = '',
  ...props
}) => {
  const radioId = props.id || `radio-${name}-${value}`;

  return (
    <div className={`flex items-center ${className}`}>
      <input
        id={radioId}
        name={name}
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={`
          h-4 w-4 border-bukawa-gray-300
          text-bukawa-primary
          focus:ring-2 focus:ring-bukawa-primary-400 focus:ring-offset-2
          ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        `.trim().replace(/\s+/g, ' ')}
        {...props}
      />
      <label
        htmlFor={radioId}
        className={`
          ml-3 text-sm font-medium text-bukawa-gray-700
          ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        `.trim().replace(/\s+/g, ' ')}
      >
        {label}
      </label>
    </div>
  );
};

/**
 * RadioGroup Component
 * Group of radio buttons
 */
export const RadioGroup = ({
  label,
  name,
  value,
  onChange,
  options = [],
  error,
  touched,
  required = false,
  helper,
  className = '',
}) => {
  const hasError = touched && error;
  const groupId = `radiogroup-${name}`;

  return (
    <div className={`mb-4 ${className}`}>
      {/* Label */}
      {label && (
        <label className="label">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Radio options */}
      <div className="space-y-2" role="radiogroup" aria-labelledby={groupId}>
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            checked={value === option.value}
            onChange={onChange}
            disabled={option.disabled}
          />
        ))}
      </div>

      {/* Helper text */}
      {helper && !hasError && (
        <p className="mt-1 text-sm text-bukawa-gray-500">
          {helper}
        </p>
      )}

      {/* Error message */}
      {hasError && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default Checkbox;